import {
    ExpandLess as CollapseIcon,
    ExpandMore as ExpandIcon,
} from '@mui/icons-material';
import { BlockTopText } from '@renderer-ui-library/molecules';
import {
    Collapse,
    Divider,
    List,
    ListItemButton,
    ListItemText,
    Typography,
} from '@renderer-ui-library/mui';
import { BlockWrapper } from '@renderer-ui-library/scaffolding/BlockWrapper/BlockWrapper';
import { BlockProps } from 'blocks/BlockProps';
import Head from 'next/head';
import React, { FC, useCallback, useMemo, useState } from 'react';
import styles from './faq.module.scss';
import { IFaqBlockData } from './IFaqBlockData';

const ListItem: FC<{
    index: number;
    isExpanded: boolean;
    onClick: (index: number) => void;
    question: string;
    answer: string;
}> = ({ index, isExpanded, onClick, question, answer }) => {
    const handleClick = useCallback(() => onClick(index), [index, onClick]);
    return (
        <>
            <ListItemButton onClick={handleClick} selected={isExpanded}>
                <Typography variant="body1" className={styles.question}>
                    {question}
                </Typography>
                {isExpanded ? <CollapseIcon /> : <ExpandIcon />}
            </ListItemButton>
            <Collapse in={isExpanded}>
                <ListItemText className={styles.listItem}>
                    <Typography variant="body1">{answer}</Typography>
                </ListItemText>
            </Collapse>
            <Divider component="li" />
        </>
    );
};

export const FaqBlock: React.FC<BlockProps<IFaqBlockData>> = React.memo(
    props => {
        const { questions, headline } = props.data;
        const [expandedAnswerIndex, setExpandedAnswerIndex] = useState<
            number | null
        >(null);

        const handleListItemClick = useCallback(
            (index: number) =>
                setExpandedAnswerIndex(prev => (prev === index ? null : index)),
            [],
        );

        const structuredData = useMemo(() => {
            const items = questions
                .filter(q => q.enableStructuredData)
                .map(item => ({
                    '@type': 'Question',
                    name: item.question,
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: `<p>${item.answer}</p>`,
                    },
                }));

            if (items.length === 0) return null;

            return JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'FAQPage',
                mainEntity: items,
            });
        }, [questions]);

        return (
            <>
                {structuredData && (
                    <Head>
                        <script type="application/ld+json">
                            {structuredData}
                        </script>
                    </Head>
                )}

                <BlockWrapper blockData={props.data}>
                    <BlockTopText
                        title={headline}
                        highlight={props.data.highlight}
                    />

                    <List>
                        {questions.map(({ question, answer }, index) => (
                            <ListItem
                                // biome-ignore lint/suspicious/noArrayIndexKey: legacy, no unique identifier
                                key={index}
                                index={index}
                                onClick={handleListItemClick}
                                question={question}
                                answer={answer}
                                isExpanded={expandedAnswerIndex === index}
                            />
                        ))}
                    </List>
                </BlockWrapper>
            </>
        );
    },
);

FaqBlock.displayName = 'FaqBlock';
